import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Settings, Eye, CheckCircle, MapPin, Camera, Upload, Trash2, Clock, Hammer, FileText, Info, ArrowRight, Save } from 'lucide-react';
import { useAppContext } from '../../hooks/useAppContext';
import { Report, ReportType, ReportStatus, MaintenanceDetails, MaintenanceImage } from '../../types';
import useAppStore from '../../store/useAppStore';
import toast from 'react-hot-toast';

type ServiceType = 'repair' | 'install' | 'preview' | 'periodic';
type WorkStatus = 'completed' | 'in_progress' | 'pending' | 'cancelled';

const serviceTypes: { id: ServiceType; label?: string; icon: React.ElementType; color: string }[] = [
    { id: 'repair', icon: Wrench, color: 'text-blue-400' },
    { id: 'install', icon: Settings, color: 'text-purple-400' },
    { id: 'preview', icon: Eye, color: 'text-emerald-400' },
    { id: 'periodic', icon: CheckCircle, color: 'text-amber-400' },
];

const workStatuses: { id: WorkStatus; label?: string; className: string }[] = [
    { id: 'completed', className: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30' },
    { id: 'in_progress', className: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
    { id: 'pending', className: 'bg-amber-500/20 text-amber-400 border-amber-500/30' },
    { id: 'cancelled', className: 'bg-red-500/20 text-red-400 border-red-500/30' },
];

interface MaintenanceReportsScreenProps {
    reportToEdit: Report | null;
}

const ModernMaintenanceReportsScreen: React.FC<MaintenanceReportsScreenProps> = ({ reportToEdit }) => {
    const { t, user } = useAppContext();
    const { addReport, updateReport } = useAppStore();
    const navigate = useNavigate();
    const [beforeImages, setBeforeImages] = useState<MaintenanceImage[]>([]);
    const [afterImages, setAfterImages] = useState<MaintenanceImage[]>([]);
    const [activeService, setActiveService] = useState<ServiceType>('repair');
    const [activeStatus, setActiveStatus] = useState<WorkStatus>('in_progress');
    const [customerName, setCustomerName] = useState('');
    const [location, setLocation] = useState('');
    const [equipment, setEquipment] = useState('');
    const [duration, setDuration] = useState('');
    const [notes, setNotes] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const isEditMode = !!reportToEdit;

    useEffect(() => {
        if (isEditMode && reportToEdit) {
            const details = reportToEdit.details as MaintenanceDetails;
            setActiveService(details.serviceType);
            setActiveStatus(details.workStatus);
            setCustomerName(details.customerName);
            setLocation(details.location);
            setEquipment(details.equipment);
            setDuration(details.duration.toString());
            setNotes(details.notes);
            setBeforeImages(details.beforeImages || []);
            setAfterImages(details.afterImages || []);
        } else {
            setActiveService('repair');
            setActiveStatus('in_progress');
            setCustomerName('');
            setLocation('');
            setEquipment('');
            setDuration('');
            setNotes('');
            setBeforeImages([]);
            setAfterImages([]);
        }
    }, [reportToEdit, isEditMode]);


    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, setter: React.Dispatch<React.SetStateAction<MaintenanceImage[]>>) => {
        if (e.target.files) {
            const newImages: MaintenanceImage[] = Array.from(e.target.files).map((file: File) => ({
                file: file,
                fileName: file.name,
                url: URL.createObjectURL(file) // For temporary preview
            }));
            setter(prev => [...prev, ...newImages]);
        }
    };

    const removeImage = (index: number, setter: React.Dispatch<React.SetStateAction<MaintenanceImage[]>>) => {
        setter(prev => prev.filter((_, i) => i !== index));
    };

    const handleGetCurrentLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const { latitude, longitude } = position.coords;
                    setLocation(`${latitude.toFixed(5)}, ${longitude.toFixed(5)}`);
                    toast.success('تم تحديد الموقع بنجاح!');
                },
                () => {
                    toast.error('لا يمكن الوصول إلى الموقع الحالي.');
                }
            );
        } else {
            toast.error('المتصفح لا يدعم خدمة تحديد المواقع.');
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!user) return;
        setIsSaving(true);

        try {
            if (isEditMode && reportToEdit) {
                const reportDetails: MaintenanceDetails = {
                    ...reportToEdit.details,
                    serviceType: activeService,
                    workStatus: activeStatus,
                    customerName,
                    location,
                    equipment,
                    duration: parseFloat(duration) || 0,
                    notes,
                };
                const updatedReport: Report = {
                    ...reportToEdit,
                    details: reportDetails,
                    modifications: [
                        ...(reportToEdit.modifications || []),
                        { modifiedBy: user.name, timestamp: new Date().toISOString() }
                    ]
                };
                await updateReport(updatedReport);
                toast.success('تم تحديث التقرير بنجاح!');
            } else {
                const reportDetails: MaintenanceDetails = {
                    serviceType: activeService,
                    workStatus: activeStatus,
                    customerName,
                    location,
                    equipment,
                    duration: parseFloat(duration) || 0,
                    notes,
                    beforeImages: beforeImages,
                    afterImages: afterImages,
                };

                const newReport: Omit<Report, 'id'> = {
                    employeeId: user.employeeId,
                    employeeName: user.name,
                    branch: user.branch,
                    department: user.department,
                    type: ReportType.Maintenance,
                    date: new Date().toISOString(),
                    status: ReportStatus.Pending,
                    details: reportDetails,
                };
                await addReport(newReport);
                toast.success('تم حفظ تقرير الصيانة بنجاح!');
            }

            navigate('/log');
        } finally {
            setIsSaving(false);
        }
    };

    const ImageUploadArea: React.FC<{
        id: string;
        title: string;
        images: MaintenanceImage[];
        onFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
        onRemove: (index: number) => void;
    }> = ({ id, title, images, onFileChange, onRemove }) => (
        <div className="space-y-3">
            <h4 className="font-semibold text-slate-300">{title}</h4>
            <div className="border-2 border-dashed border-slate-700 hover:border-blue-500/50 rounded-xl p-6 text-center bg-slate-800/30 transition-all group cursor-pointer" onClick={() => document.getElementById(id)?.click()}>
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                    <Camera size={24} className="text-blue-400" />
                </div>
                <p className="text-sm text-slate-400 mb-2">اضغط لرفع الصور</p>
                <input type="file" id={id} multiple accept="image/*" className="hidden" onChange={onFileChange} />
            </div>
            {images.length > 0 && (
                <div className="grid grid-cols-3 gap-3 mt-3">
                    {images.map((img, index) => (
                        <div key={index} className="relative group aspect-square rounded-lg overflow-hidden border border-white/10">
                            <img src={img.url} alt={img.fileName} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <button
                                    onClick={(e) => { e.stopPropagation(); onRemove(index); }}
                                    className="p-2 bg-red-500/80 text-white rounded-full hover:bg-red-500 transition-colors"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-3xl font-bold modern-gradient-text mb-2">{isEditMode ? "تعديل تقرير صيانة" : "تقرير صيانة / ضمان"}</h1>
                    <p className="text-slate-400">توثيق أعمال الصيانة والضمان</p>
                </div>
                <button
                    onClick={() => navigate(isEditMode ? '/log' : '/')}
                    className="px-4 py-2 rounded-xl glass-card hover:bg-white/10 text-slate-300 transition-all flex items-center gap-2"
                >
                    <ArrowRight size={18} />
                    <span>رجوع</span>
                </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Service Type & Status */}
                <div className="glass-card p-6 rounded-2xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <Wrench className="text-blue-400" size={24} />
                        تفاصيل الخدمة
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">نوع الخدمة</label>
                            <div className="grid grid-cols-2 gap-3">
                                {serviceTypes.map(({ id, icon: Icon, color }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setActiveService(id)}
                                        className={`p-3 rounded-xl border transition-all flex items-center gap-3 ${activeService === id
                                            ? 'bg-blue-600/20 border-blue-500/50 text-white shadow-lg shadow-blue-500/10'
                                            : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:bg-slate-800/50'
                                            }`}
                                    >
                                        <Icon size={18} className={activeService === id ? 'text-blue-400' : ''} />
                                        <span className="text-sm font-medium">
                                            {t(
                                                id === 'repair' ? 'maintenanceServiceRepair' :
                                                    id === 'install' ? 'maintenanceServiceInstall' :
                                                        id === 'preview' ? 'maintenanceServicePreview' :
                                                            'maintenanceServicePeriodic'
                                            )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-3">حالة العمل</label>
                            <div className="grid grid-cols-2 gap-3">
                                {workStatuses.map(({ id, className }) => (
                                    <button
                                        key={id}
                                        type="button"
                                        onClick={() => setActiveStatus(id)}
                                        className={`p-3 rounded-xl border transition-all flex items-center justify-center gap-2 ${activeStatus === id
                                            ? className
                                            : 'bg-slate-800/30 border-slate-700/50 text-slate-400 hover:bg-slate-800/50'
                                            }`}
                                    >
                                        <span className="text-sm font-medium">
                                            {t(
                                                id === 'completed' ? 'workStatusCompleted' :
                                                    id === 'in_progress' ? 'workStatusInProgress' :
                                                        id === 'pending' ? 'workStatusPending' :
                                                            'workStatusCancelled'
                                            )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Customer & Location */}
                <div className="glass-card p-6 rounded-2xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <MapPin className="text-purple-400" size={24} />
                        بيانات العميل والموقع
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">اسم العميل</label>
                            <input
                                type="text"
                                value={customerName}
                                onChange={e => setCustomerName(e.target.value)}
                                required
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 px-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                placeholder="الاسم الكامل"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">الموقع</label>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={location}
                                    onChange={e => setLocation(e.target.value)}
                                    className="flex-1 bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 px-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="الإحداثيات أو العنوان"
                                />
                                <button
                                    type="button"
                                    onClick={handleGetCurrentLocation}
                                    className="p-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-blue-400 hover:bg-blue-500/10 transition-colors"
                                    title="تحديد الموقع الحالي"
                                >
                                    <MapPin size={20} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Technical Details */}
                <div className="glass-card p-6 rounded-2xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <Hammer className="text-amber-400" size={24} />
                        تفاصيل العمل التقنية
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">المعدات المستخدمة</label>
                            <div className="relative">
                                <Hammer className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <input
                                    type="text"
                                    value={equipment}
                                    onChange={e => setEquipment(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="مثال: مفكات، جهاز قياس..."
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-400 mb-2">مدة العمل (بالساعات)</label>
                            <div className="relative">
                                <Clock className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                                <input
                                    type="number"
                                    value={duration}
                                    onChange={e => setDuration(e.target.value)}
                                    className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 pr-10 pl-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50"
                                    placeholder="مثال: 3.5"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-slate-400 mb-2">الملاحظات الفنية</label>
                            <textarea
                                value={notes}
                                onChange={e => setNotes(e.target.value)}
                                className="w-full bg-slate-800/50 border border-slate-700/50 rounded-xl py-3 px-4 text-white placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[100px]"
                                placeholder="أضف تفاصيل فنية عن العمل المنجز..."
                            />
                        </div>
                    </div>
                </div>

                {/* Images */}
                <div className="glass-card p-6 rounded-2xl">
                    <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                        <Camera className="text-emerald-400" size={24} />
                        الصور المرفقة
                    </h2>
                    {!isEditMode ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <ImageUploadArea id="before-upload" title="صور قبل العمل" images={beforeImages} onFileChange={(e) => handleFileChange(e, setBeforeImages)} onRemove={(i) => removeImage(i, setBeforeImages)} />
                            <ImageUploadArea id="after-upload" title="صور بعد العمل" images={afterImages} onFileChange={(e) => handleFileChange(e, setAfterImages)} onRemove={(i) => removeImage(i, setAfterImages)} />
                        </div>
                    ) : (
                        <div className="p-4 bg-blue-500/10 border border-blue-500/20 text-blue-300 rounded-xl flex items-center gap-3">
                            <Info size={20} />
                            <p className="text-sm">لا يمكن تعديل الصور المرفقة في وضع التحرير. لعرض الصور الحالية، يرجى الرجوع إلى تفاصيل التقرير.</p>
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                    <button
                        type="submit"
                        disabled={isSaving}
                        className="px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold hover:from-blue-500 hover:to-blue-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/20 flex items-center gap-2"
                    >
                        {isSaving ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Save size={20} />}
                        <span>{isEditMode ? "حفظ التعديلات" : "حفظ التقرير"}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ModernMaintenanceReportsScreen;
